import { useState, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import Button from 'components/base/Button';
import { Department } from 'store/departmentmanagementSlice';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

interface Props {
  show: boolean;
  handleClose: () => void;
  dept: Department;
  onSave: (updated: {
    id: number;
    name: string;
    head_user_id: number; // Change back to number (not null)
    company_id: number;
  }) => void;
}

const EditDepartmentModal = ({ show, handleClose, dept, onSave }: Props) => {
  const { data: users } = useSelector((state: RootState) => state.users);
  const { data: companies } = useSelector(
    (state: RootState) => state.companies
  );

  const [name, setName] = useState(dept.name);
  const [headUserId, setHeadUserId] = useState<number>(dept.head_user_id); // Change back to number
  const [companyId, setCompanyId] = useState<number>(dept.company_id);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setName(dept.name);
    setHeadUserId(dept.head_user_id);
    setCompanyId(dept.company_id);
  }, [dept]);

  // Filter users by selected company
  const companyUsers = users.filter(user => user.company_id === companyId);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Convert empty selection to 0 instead of null
    const headUserIdToSend = headUserId === 0 ? 0 : headUserId;

    await onSave({
      id: dept.id,
      name,
      head_user_id: headUserIdToSend, // Send 0 instead of null
      company_id: companyId
    });

    setLoading(false);
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Department</Modal.Title>
      </Modal.Header>

      <Form onSubmit={onSubmit}>
        <Modal.Body>
          {/* Company Selection Field - Disabled */}
          <Form.Group className="mb-3">
            <Form.Label>Company</Form.Label>
            <Form.Control
              value={
                companies.find(c => c.id === companyId)?.name ||
                `Company #${companyId}`
              }
              disabled
              readOnly
            />
            <Form.Text className="text-muted">
              Company cannot be changed for existing departments
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Department Name *</Form.Label>
            <Form.Control
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Department Head (Optional)</Form.Label>
            <Form.Select
              value={headUserId}
              onChange={e => setHeadUserId(Number(e.target.value))}
            >
              <option value={0}>No Department Head</option>{' '}
              {/* Use 0 instead of empty string */}
              {companyUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </Form.Select>
            {companyUsers.length === 0 && (
              <Form.Text className="text-warning">
                No users found in the selected company
              </Form.Text>
            )}
            <Form.Text className="text-muted">
              Optional: Select a user to be the department head
            </Form.Text>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditDepartmentModal;
